import {
  buildScorecardAuthoringReadiness,
  resolveScorecardAuthoringProfile,
} from "../../components/composables/scorecardAuthoringProfiles";
import {
  completeLoadMeta,
  createIdleLoadMeta,
  failLoadMeta,
  startLoadMeta,
} from "../../services/firebase/loadState";
import {
  appendScorecardArrayField,
  deleteScorecardRecord,
  deleteScorecardsForClub,
  openScorecard,
  saveScorecardPatch,
  updateScorecardRecord,
} from "../../services/firebase/firestoreDataLayer";

const scoreCardHeader = {
  namespaced: true,

  state() {
    return {
      /* Some of the score card structure is defined here, but it may not be an exact match for what is created when a score card is created for a game */

      scoreCard: {
        //linked club id
        ccID: null,
        //linked club name
        ccName: null,
        //date score card created
        dateCreated: null,
        //id of game this score card is for
        gdID: null,
        //this link is used to determine which vue file to load
        vueFileToLoad: null,
        //Name of game being scored
        gdName: null,
        //players on score card
        players: [{ id: null, name: null, lastDealer: 0 }],
        //Record of last dealer
        lastDealer: 0,
        //Name of score card shown screen.
        scoreCardName: null,
        //id of member owner of score card
        siteMemberName: null,
        //Name of member owner of score card
        siteMemberID: null,
        //array of on score card. Not applicable to every scorecard
        teams: [{ id: null, name: null }],

        /* Games and hands are used by every score card, but will have data
        specific to the game being scored */
        games: [],
        matches: [],
        hands: [],
      },
      //List of score cards for current user
      scoreCardList: [],
      scoreCardNames: [],
      gameData: null,
      scoreCardLoaded: false,
      scoreCardID: null,
      showDealOrderDialog: false,
      showCustomizeGameDialog: false,

      /* This data will be set to "new" if a new game had just been created
      and to loaded if a saved score card is being loaded. */
      scoreCardOrigination: null,
      loadMeta: createIdleLoadMeta(),
      lastRequestedScoreCardId: null,
    };
  },

  //Getters for scoreCards
  getters: {
    //Return the scoreCardList
    scoreCardList(state) {
      return state.scoreCardList;
    },

    //Return the list of scoreCardNames, used in UI select scorecard control
    scoreCardNames(state) {
      if (state.scoreCardNames.length === 0) {
        state.scoreCardNames.push("---");
      }
      return state.scoreCardNames;
    },

    //Returns the ID of the last dealer
    lastDealer(state) {
      return state.scoreCard.lastDealer;
    },

    //Returns the state of the showDealOrderDialog variable
    showDealOrderDialog(state) {
      return state.showDealOrderDialog;
    },

    //Returns the state of the showCustomizeGameDialog variable
    showCustomizeGameDialog(state) {
      return state.showCustomizeGameDialog;
    },

    //Returns the state of the scoreCardLoaded variable
    scoreCardLoaded(state) {
      return state.scoreCardLoaded;
    },

    //Returns the full score card object
    scoreCard(state) {
      return state.scoreCard;
    },

    //Returns the score card ID
    scoreCardID(state) {
      return state.scoreCardID;
    },

    //Returns the game data for the game being scored
    gameData(state) {
      return state.gameData;
    },

    //Returns the score card origination state.  Used to determine if a new score card is being created or a saved score card is being loaded
    scoreCardOrigination(state) {
      return state.scoreCardOrigination;
    },

    //Returns the name of the game being scored by the score card
    gameName(state) {
      return state.scoreCard.gdName;
    },

    //Returns the name vue file to open when loading score cardr
    vueFileToLoad(state) {
      if (state.scoreCard.vueFileToLoad === undefined) {
        return false;
      } else {
        return state.scoreCard.vueFileToLoad;
      }
    },

    //Returns the internal authoring profile for the loaded score card
    scoreCardAuthoringProfile(state) {
      return resolveScorecardAuthoringProfile(state.scoreCard.vueFileToLoad);
    },

    //Returns whether the loaded score card maps cleanly to the authoring-proof scaffolding
    scoreCardAuthoringProfileReadiness(state) {
      return buildScorecardAuthoringReadiness(state.scoreCard.vueFileToLoad);
    },

    //Returns the ID of the score card from the score card name
    getScoreCardIDFromName: (state) => (name) => {
      let ogID = state.scoreCardList.filter((og) => og.name === name);
      //if score card not found return false
      if (ogID.length === 0) {
        return false;
      }
      return ogID[0].id;
    },

    //Returns the name of the score card from the score card id
    getScoreCardNameFromID: (state) => (id) => {
      /* Check for number of open score cards. If no open score cards return false otherwise set default to first open score card in list */
      let defaultScorecard = "";
      if (state.scoreCardList.length > 0) {
        /* If at least one score card in array, set default scorecard return value to first scorecard in array in case last used scorecard is not found (eg. deleted) */
        defaultScorecard = state.scoreCardList[0].name;
      }

      //if id is null return defaultScorecard
      if (id === null) {
        return defaultScorecard;
      }
      //if no score cards return defaultScorecard
      if (state.scoreCardList === false) {
        return defaultScorecard;
      }
      //find open game by id
      let ogName = state.scoreCardList.filter((og) => og.id === id);
      //if open game not found return defaultscorecard
      if (ogName.length === 0) {
        return defaultScorecard;
      }
      return ogName[0].name;
    },

    loadState(state) {
      return state.loadMeta.loadState;
    },

    loadError(state) {
      return state.loadMeta.error;
    },

    lastRequestedScoreCardId(state) {
      return state.lastRequestedScoreCardId;
    },
  },

  mutations: {
    //Update the last dealer
    updateLastDealer(state, dealer) {
      state.scoreCard.lastDealer = dealer;
    },

    //Update the showDealOrderDialog variable to show or hide the dialog
    showDealOrderDialog(state, payLoad) {
      state.showDealOrderDialog = payLoad;
    },

    //Update the showCustomizeGameDialog variable to show or hide the dialog
    showCustomizeGameDialog(state, payLoad) {
      state.showCustomizeGameDialog = payLoad;
    },

    //Add a hand to the score card display object
    addHand(state, payLoad) {
      state.scoreCard.hands.push(payLoad);
    },

    //Update a hand to the score card display object
    updateHand(state, payLoad) {
      state.scoreCard.hands = payLoad;
    },

    //Add a game to the score card display object
    addGame(state, payLoad) {
      state.scoreCard.games.push(payLoad);
    },

    addMatch(state, payLoad) {
      if (!Array.isArray(state.scoreCard.matches)) {
        state.scoreCard.matches = [];
      }
      state.scoreCard.matches.push(payLoad);
    },

    //Update the player order to the score card display object
    updatePlayerOrder(state, players) {
      state.scoreCard.players = players;
    },

    //Update the variable to indicate if the score card is new or being reloaded
    scoreCardOrigination(state, origin) {
      state.scoreCardOrigination = origin;
    },

    //Update the score card display object with the list of score cards
    getListOfScoreCards(state, payLoad) {
      state.scoreCardList = payLoad;

      //Create a list of score card names for use in UI select control.  Sort the list alphabetically
      let ogName = [];
      if (state.scoreCardList === false) {
        state.scoreCardNames = ogName;
        return;
      }

      state.scoreCardList.forEach((og) => {
        let ogn = og.name;
        ogName.push(ogn);
      });

      //Sort by name before returning
      ogName.sort(function (a, b) {
        return a.localeCompare(b);
      });

      state.scoreCardNames = ogName;
    },

    createScoreCard(state, newScoreCard) {
      state.scoreCard = newScoreCard;
      state.scoreCardID = state.scoreCard.id;
      //change number of scorecards to trigger update to list on home page
    },

    //Load the game data for the game being scored
    loadGameData(state, gameData) {
      state.gameData = gameData;
      //Change state to indicate that score card data has been loaded
      state.scoreCardLoaded = true;
    },

    syncLoadedScoreCardGameLink(state, gameData) {
      if (!gameData) {
        return;
      }

      state.scoreCard.gdID = gameData.id;
      state.scoreCard.gdName = gameData.name;
      state.scoreCard.vueFileToLoad = gameData.vueFileToLoad;
    },

    //Indicate that score card data is not loaded
    scoreCardLoading(state) {
      state.scoreCardLoaded = false;
      state.gameData = null;
    },

    scoreCardLoadStarted(state, payload) {
      state.loadMeta = startLoadMeta(payload.requestKey);
      state.lastRequestedScoreCardId = payload.scoreCardId ?? null;
    },

    scoreCardLoadSucceeded(state, result) {
      state.loadMeta = completeLoadMeta(state.loadMeta, result);
    },

    scoreCardLoadFailed(state, error) {
      state.loadMeta = failLoadMeta(state.loadMeta, error);
    },

    //Update the score card name in the score card display object
    changeScoreCardName(state, payLoad) {
      state.scoreCard.scoreCardName = payLoad.name;
      if (!Array.isArray(state.scoreCardList)) {
        return;
      }

      state.scoreCardList = state.scoreCardList.map((scoreCard) => {
        if (scoreCard.id !== payLoad.id) {
          return scoreCard;
        }

        return {
          ...scoreCard,
          name: payLoad.name,
        };
      });

      state.scoreCardNames = state.scoreCardList
        .map((scoreCard) => scoreCard.name)
        .sort((a, b) => a.localeCompare(b));
    },

    //Delete the hands for a game from the score card display object
    deleteHands(state, filteredHands) {
      /* Save hands to memory with the deleted hands already removed  */
      state.scoreCard.hands = filteredHands;
    },

    resetScoreCardListState(state) {
      state.scoreCardList = [];
      state.scoreCardNames = [];
      state.loadMeta = createIdleLoadMeta();
    },
  },

  /*  Database reads/writes are in actions.  */
  actions: {
    async createScoreCard({ dispatch }) {
      return dispatch("createScorecard", {}, { root: true });
    },

    async loadScoreCard({ dispatch }, id) {
      return dispatch("openScorecard", id, { root: true });
    },

    /* loads data for card game being playerd.  Must be called after score card header has been created so game ID is available*/
    async loadGameData({ commit, state, rootGetters = {}, dispatch, rootState }) {
      if (!state.scoreCard?.id) {
        commit("loadGameData", null);
        return;
      }

      if (
        rootState?.scorecardRoute?.loadState === "ready" &&
        state.gameData &&
        state.scoreCardLoaded
      ) {
        return state.gameData;
      }

      if (rootState?.scorecardRoute?.loadState === "loading") {
        return dispatch("openScorecard", state.scoreCard.id, { root: true });
      }

      const result = await openScorecard(
        state.scoreCard.id,
        rootGetters?.["cg/gamesData"]
      );

      if (result.error) {
        commit("scoreCardLoadFailed", result.error);
        return;
      }

      commit("syncLoadedScoreCardGameLink", result.data.gameData);
      commit("loadGameData", result.data.gameData);
      commit("scoreCardLoadSucceeded", result);
      return result.data.gameData;
    },

    //Get list of score cards for current user
    async getListOfScoreCards({ dispatch, rootGetters, rootState }) {
      const userId = rootGetters["auth/loggedInSiteMemberID"];
      if (
        rootState?.homeContextUserId === userId &&
        rootState?.homeBootstrap?.loadState === "ready"
      ) {
        return;
      }

      await dispatch("initializeHome", {}, { root: true });
    },

    //Add a hand to the score card
    async addHand({ getters, commit }, hand) {
      const result = await appendScorecardArrayField(
        getters["scoreCardID"],
        "hands",
        hand
      );
      if (result.error) {
        throw result.error;
      }
      commit("addHand", hand);
    },

    //update a hand to the score card.  Replace the hand with the same game number
    async updateHand({ getters, commit }, hand) {
      const result = await saveScorecardPatch(getters["scoreCardID"], {
        hands: hand,
      });
      if (result.error) {
        throw result.error;
      }
      commit("updateHand", hand);
    },

    //Update the player order
    async updatePlayerOrder({ getters, commit }, players) {
      const result = await saveScorecardPatch(getters["scoreCardID"], {
        players,
      });
      if (result.error) {
        throw result.error;
      }
      commit("updatePlayerOrder", players);
    },

    //Update the last dealer
    async updateLastDealer({ getters, commit }, dealer) {
      const result = await saveScorecardPatch(getters["scoreCardID"], {
        lastDealer: dealer,
      });
      if (result.error) {
        throw result.error;
      }
      commit("updateLastDealer", dealer);
    },

    //Add a game to the score card
    async addGame({ getters, commit }, game) {
      const result = await appendScorecardArrayField(
        getters["scoreCardID"],
        "games",
        game
      );
      if (result.error) {
        throw result.error;
      }
      commit("addGame", game);
    },

    async addMatch({ getters, commit }, match) {
      const result = await appendScorecardArrayField(
        getters["scoreCardID"],
        "matches",
        match
      );
      if (result.error) {
        throw result.error;
      }
      commit("addMatch", match);
    },

    //Update the score card name
    async changeScoreCardName({ commit }, payLoad) {
      const result = await updateScorecardRecord(payLoad.id, {
        scoreCardName: payLoad.name,
      });
      if (result.error) {
        throw result.error;
      }

      commit("changeScoreCardName", payLoad);
    },

    //save customizable data after user edits
    async saveCustomizableData({ getters }, payLoad) {
      const result = await updateScorecardRecord(getters["scoreCardID"], {
        customizable: payLoad.customizable,
      });
      if (result.error) {
        throw result.error;
      }
      //commit not needed because scoreCard customizable data was edited in place
    },

    //Delete the referenced score card
    async deleteScoreCard({ dispatch }, id) {
      const result = await deleteScorecardRecord(id);
      if (result.error) {
        throw result.error;
      }
      await dispatch("refreshHomeContext", null, { root: true });
    },

    //Delete all score cards for the referenced card club
    async deleteAllScoreCards({ dispatch }, id) {
      //query for and deleted all club players owned by card club
      const result = await deleteScorecardsForClub(id);
      if (result.error) {
        throw result.error;
      }

      await dispatch("refreshHomeContext", null, { root: true });
    },

    //Delete hands for a game from the score card
    async deleteHands({ commit, state }, payLoad) {
      /* filter out the hand records that are to be deleted.  Then rewrite the remaining hands.  Firebase does not easily allow removing individual records in arrays */
      let filteredHands = state.scoreCard.hands.filter((hand) => {
        if (hand.gameNumber != payLoad.gameNumber) {
          return true;
        }
      });

      const result = await saveScorecardPatch(payLoad.ID, { hands: filteredHands });
      if (result.error) {
        throw result.error;
      }

      commit("deleteHands", filteredHands);
    },

    //Edit a score for a hand
    async editHand({ state }, payLoad) {
      /* Copy the hands array into a new array.  change the one game/hand/player value that has been edited */
      let editedHands = [];
      //Find the game number and hand row
      state.scoreCard.hands.forEach((hand) => {
        if (
          hand.gameNumber === payLoad.gameNumber &&
          hand.handNumber === payLoad.row
        ) {
          //Find the player column and edit the score
          hand.scores[payLoad.column].score = payLoad.newValue;
        }
        //Add the hand to the new array
        editedHands.push(hand);
      });

      //Write the new array to the database
      const result = await saveScorecardPatch(payLoad.ID, { hands: editedHands });
      if (result.error) {
        throw result.error;
      }
    },
  },
};

export default scoreCardHeader;
